import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useRef, useState } from 'react';
import { parsePastedPrescription } from '@lensmira/domain';
import { BASE_DIRECTIONS, COLUMNS, COLUMN_KIND, EMPTY, EMPTY_MARK, ROWS, ROW_LABEL, accept, cellId, copyRow, displayText, entered, nextCell, rawText, step, toggleSign, } from './rx-value.js';
export function emptyGrid() {
    const blank = () => {
        const row = {};
        for (const column of COLUMNS)
            row[column] = EMPTY;
        return row;
    };
    return { od: blank(), os: blank() };
}
/**
 * `RXW-002`'s keyboard rules, `RX-07`'s, and `KB-03`'s constraint that none of them may collide
 * with a browser or screen reader binding.
 *
 * The spacebar as the sign toggle is a choice this repo made and the spec left open; `rx-value.ts`
 * carries the reasoning. `Ctrl+D` for "copy OD to OS" is the same kind of choice: d for down, and
 * the browser's own Ctrl+D (bookmark) is preventable and not something a counter uses.
 */
export function RxGrid({ value, onChange, warnings, onPaste, disabled = false, }) {
    const [focused, setFocused] = useState(null);
    const refs = useRef(new Map());
    const focus = useCallback((address) => {
        refs.current.get(cellId(address.row, address.column))?.focus();
    }, []);
    const put = useCallback((row, column, next) => {
        onChange({ ...value, [row]: { ...value[row], [column]: next } });
    }, [onChange, value]);
    const handleKey = useCallback((event, row, column) => {
        const kind = COLUMN_KIND[column];
        const current = value[row][column];
        // Arrow keys step the value. RX-07: "arrows step 0.25". Shift is the coarse step.
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            if (disabled)
                return;
            const direction = event.key === 'ArrowUp' ? 1 : -1;
            put(row, column, step(current, kind, direction, event.shiftKey));
            return;
        }
        // RX-07: "one key toggles sign".
        if (event.key === ' ' && kind === 'power') {
            event.preventDefault();
            if (disabled)
                return;
            put(row, column, toggleSign(current, kind));
            return;
        }
        // RXW-002: "copy OD to OS".
        if (event.key.toLowerCase() === 'd' && (event.ctrlKey || event.metaKey)) {
            event.preventDefault();
            if (disabled)
                return;
            onChange({ ...value, os: copyRow(value.od) });
            return;
        }
        // AI-001: a proposal becomes a value only by a human accepting it. Enter on a proposed cell
        // is that act, and there is no other route.
        if (event.key === 'Enter' && current.kind === 'proposed') {
            event.preventDefault();
            if (disabled)
                return;
            put(row, column, accept(current));
            return;
        }
        // Tab is the browser's, except that the grid's order is not the DOM's: RXW-002 wants OD then
        // OS by field, and the DOM is laid out by row because RX-08 fixes the visual order. So the
        // grid drives it explicitly rather than by tabindex arithmetic that breaks when a column moves.
        if (event.key === 'Tab') {
            const target = nextCell({ row, column }, event.shiftKey ? -1 : 1);
            if (target) {
                event.preventDefault();
                focus(target);
            }
            return;
        }
        // Left and right move between cells in the same row, which is what the eye expects from a
        // grid even though it is not the tab order.
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            const index = COLUMNS.indexOf(column);
            const target = COLUMNS[index + (event.key === 'ArrowRight' ? 1 : -1)];
            if (target) {
                event.preventDefault();
                focus({ row, column: target });
            }
        }
    }, [disabled, focus, onChange, put, value]);
    const handlePaste = useCallback((event) => {
        const text = event.clipboardData.getData('text');
        if (!text.trim())
            return;
        event.preventDefault();
        if (disabled)
            return;
        const result = parsePastedPrescription(text);
        const next = { od: { ...value.od }, os: { ...value.os } };
        for (const row of ROWS) {
            const eye = result[row];
            if (!eye)
                continue;
            if (eye.sphere !== undefined)
                next[row].sphere = entered((eye.sphere / 4).toFixed(2));
            if (eye.cylinder !== undefined)
                next[row].cylinder = entered((eye.cylinder / 4).toFixed(2));
            if (eye.axis !== undefined)
                next[row].axis = entered(String(eye.axis));
            if (eye.add !== undefined)
                next[row].add = entered((eye.add / 4).toFixed(2));
        }
        onChange(next);
        onPaste?.(result);
    }, [disabled, onChange, onPaste, value]);
    const registerRef = useCallback((id, element) => {
        if (element)
            refs.current.set(id, element);
        else
            refs.current.delete(id);
    }, []);
    /**
     * **No positive `tabIndex` anywhere in this grid, deliberately.**
     *
     * It used to carry `tabIndex={1..12}` to express `RXW-002`'s order. Positive values are not a
     * local statement: they jump the element ahead of every `tabindex="0"` element on the page, so
     * the grid captured focus before anything above it in the document. A prescription-type selector
     * added above the grid then tabbed *after* the whole grid, which is a focus order that disagrees
     * with the visual order, and `KB-02`'s "visible focus ring" is worth nothing if focus arrives
     * somewhere the eye is not.
     *
     * The order is enforced by the Tab handler above instead, which is where it belonged: it knows
     * `TAB_ORDER` and the DOM does not, and at the two ends it hands back to the browser so focus
     * leaves the grid in document order.
     *
     * Found by adding one `<select>` above the grid and watching release gate 1 stop reaching the
     * save button.
     */
    return (
    /*
     * **The grid gets somewhere to overflow into rather than being compressed.**
     *
     * `table-layout: auto` sizes each column to what fits, so in a 497px container six columns
     * came out 71 to 83px wide and every cell holding a value clipped it: `-2.25` wanted 76px of
     * content in 73px of cell. `RX-03` is "the sign is always rendered, including `+`. The most
     * expensive typo in the product", and a clipped cell is how a minus goes missing. §5 adds that
     * the grid must be usable at every breakpoint, down to 360.
     *
     * Six columns of six monospace characters at `--lm-text-data-lg` cannot fit 800px beside the
     * rail, so one of three things has to give: the data size (`RX-01`), the sign (`RX-03`), or
     * the grid fitting without a scroll. Scrolling is the only one the spec does not make a MUST.
     */
    _jsx("div", { className: "lm-rx-grid__scroll", children: _jsxs("table", { className: "lm-rx-grid", dir: "ltr", children: [_jsx("caption", { className: "lm-rx-grid__caption", children: "Prescription" }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", children: _jsx("span", { className: "lm-visually-hidden", children: "Eye" }) }), COLUMNS.map((column) => (_jsx("th", { scope: "col", className: "lm-rx-grid__head", children: column === 'sphere' ? 'sph' : column === 'cylinder' ? 'cyl' : column }, column)))] }) }), _jsx("tbody", { children: ROWS.map((row) => (_jsxs("tr", { children: [_jsxs("th", { scope: "row", className: "lm-rx-grid__row-label", children: [_jsx("span", { className: "lm-rx-grid__abbr", children: ROW_LABEL[row].abbreviation }), _jsx("span", { className: "lm-rx-grid__word", children: ROW_LABEL[row].word })] }), COLUMNS.map((column) => {
                                const id = cellId(row, column);
                                const cell = value[row][column];
                                const isFocused = focused?.row === row && focused.column === column;
                                const warning = warnings?.[id];
                                const kind = COLUMN_KIND[column];
                                return (_jsxs("td", { className: "lm-rx-grid__cell", "data-proposed": cell.kind === 'proposed' ? 'true' : undefined, "data-warn": warning ? 'true' : undefined, children: [_jsx("input", { id: id, ref: (element) => registerRef(id, element), className: "lm-rx-cell", "aria-label": `${ROW_LABEL[row].word} eye ${column}`, "aria-invalid": warning ? true : undefined, "aria-describedby": warning ? `${id}-warn` : undefined, disabled: disabled, inputMode: kind === 'base' ? 'text' : 'decimal', autoComplete: "off", spellCheck: false, list: kind === 'base' ? 'lm-base-directions' : undefined, 
                                            // RX-04: empty shows an em dash and never a blank, because blank is
                                            // indistinguishable from unsaved. It is the placeholder rather than the value,
                                            // so the cell is genuinely empty and typing does not have to clear it first.
                                            placeholder: EMPTY_MARK, value: isFocused ? rawText(cell) : displayText(cell, kind), onFocus: (e) => {
                                                setFocused({ row, column });
                                                // RX-07: "focus fills the whole cell." Selecting on focus is what makes
                                                // overtyping a value one keystroke instead of three.
                                                e.currentTarget.select();
                                            }, onBlur: () => setFocused(null), onChange: (e) => put(row, column, entered(e.currentTarget.value)), onKeyDown: (e) => handleKey(e, row, column), onPaste: handlePaste }), cell.kind === 'proposed' ? (
                                        // RX-09: an AI-proposed value is visually distinct from a confirmed one until a
                                        // human accepts it. The marker is text as well as colour, per CL-02.
                                        _jsxs("span", { className: "lm-rx-cell__proposed", "aria-live": "polite", children: ["proposed by ", cell.source, ", press Enter to accept"] })) : null, warning ? (
                                        // RX-05: a warn tint, a left edge and an inline reason. Never a block.
                                        _jsx("span", { id: `${id}-warn`, className: "lm-rx-cell__warn", role: "status", children: warning })) : null] }, column));
                            })] }, row))) }), _jsx("datalist", { id: "lm-base-directions", children: BASE_DIRECTIONS.map((d) => (_jsx("option", { value: d }, d))) })] }) }));
}
//# sourceMappingURL=RxGrid.js.map